/** TikTok analytics (WS4 routes `/api/analytics/tiktok/*`): which songs TikTok uses, and which part of them.
 * Songs table (sortable by views / uses / average) → a song's segments (per section, per song window, unknown) with
 * its views over time; the watched profiles (artist handles, fan pages). Every number is in a text table; each table
 * has a CSV / Markdown twin for agents. Videos are split by who posted them: ours (our handles: the artists' profiles
 * and our posting accounts) are our views; videos other creators made with our sounds are tracked separately and
 * never counted in our views. */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, type Schemas, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { useAnnounce } from "../../components/announce";
import { PageHeader } from "../../components/PageHeader";
import { apiFileUrl } from "../../lib/identity";
import { Sparkline } from "../lib/charts";
import { fmtDuration, fmtNum, fmtWhen } from "../lib/time";
import { Empty, QueryView, Section, TableScroll } from "../lib/ui";
import { intParam, oneOf, type Patch, useUrlState } from "../lib/url";

type Song = Schemas["TtSongAnalytics"];
type SegRow = Schemas["TtSegmentRow"];
type Account = Schemas["TtAccountAnalytics"];
type Others = Schemas["TtSongOthers"];

export const SONG_SORTS = ["views", "uses", "avg", "others"] as const;
export type SongSort = (typeof SONG_SORTS)[number];
export type TikTokParams = { sort: SongSort; song?: number };

export function parseTikTokParams(sp: URLSearchParams): TikTokParams {
  return { sort: oneOf(sp.get("sort"), SONG_SORTS, "views"), song: intParam(sp.get("song")) };
}

export function tiktokParamsToPatch(p: TikTokParams): Patch {
  return { sort: p.sort === "views" ? undefined : p.sort, song: p.song };
}

const SORT_KEY: Record<SongSort, (s: Song) => number> = {
  views: (s) => s.views_total,
  uses: (s) => s.uses,
  others: (s) => s.others.views_total,
  avg: (s) => s.views_avg ?? -1,
};

/** Most first; ties by song id so the order is stable. */
export function sortSongs(rows: Song[], by: SongSort): Song[] {
  const key = SORT_KEY[by];
  return [...rows].sort((a, b) => key(b) - key(a) || a.song_id - b.song_id);
}

function fmtAvg(v: number | null | undefined): string {
  return v == null ? "" : fmtNum(Math.round(v));
}

function fmtSpan(a: number | null | undefined, b: number | null | undefined): string {
  return a == null || b == null ? "" : `${fmtDuration(a)}–${fmtDuration(b)}`;
}

/** A test-id-safe slug: "other creators' videos" → "other-creators-videos". */
export function slug(s: string): string {
  return s.toLowerCase().replace(/['’]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function Csv({ href, what }: { href: string; what: string }) {
  const id = slug(what);
  return (
    <span className="ws7-inline">
      <a href={apiFileUrl(`${href}.csv`, { download: 1 })} data-testid={`csv-${id}`}>CSV</a>{" "}
      <a href={apiFileUrl(`${href}.md`)} data-testid={`md-${id}`} target="_blank" rel="noreferrer">Markdown</a>
    </span>
  );
}

function SortHeader({ by, title, sort, onSort }: { by: SongSort; title: string; sort: SongSort; onSort: (s: SongSort) => void }) {
  return (
    <th scope="col" className="num" aria-sort={sort === by ? "descending" : undefined}>
      <button type="button" className="ws7-sort" data-testid={`tt-sort-${by}`} aria-label={`Sort by ${title}`} onClick={() => onSort(by)}>
        {title}
        {sort === by && <span aria-hidden="true"> ↓</span>}
      </button>
    </th>
  );
}

function SongsTable({ rows, sort, selected, onSort, onPick }: {
  rows: Song[];
  sort: SongSort;
  selected?: number;
  onSort: (s: SongSort) => void;
  onPick: (s: Song) => void;
}) {
  return (
    <TableScroll label="TikTok songs table">
      <table className="ws7-table" aria-label="TikTok use per song" data-testid="tt-songs-table">
        <thead>
          <tr>
            <th scope="col">Song</th>
            <th scope="col" className="num">Sounds</th>
            <SortHeader by="uses" title="Our videos" sort={sort} onSort={onSort} />
            <SortHeader by="views" title="Our views" sort={sort} onSort={onSort} />
            <SortHeader by="avg" title="Our avg views" sort={sort} onSort={onSort} />
            <th scope="col" className="num">Others' videos</th>
            <SortHeader by="others" title="Others' views" sort={sort} onSort={onSort} />
            <th scope="col" className="num">Other creators</th>
            <th scope="col" className="num">TikTok uses</th>
            <th scope="col" className="num">Coverage</th>
            <th scope="col" className="num">Known segment</th>
            <th scope="col">Top video</th>
            <th scope="col">Harvested</th>
            <th scope="col">Segments</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const top = s.top_items?.[0];
            return (
              <tr key={s.song_id} data-testid={`tt-song-${s.song_id}`} className={selected === s.song_id ? "ws7-selected" : undefined}>
                <th scope="row"><Link to={`/songs/${s.song_id}`}>{s.title}</Link> <span className="ws7-muted">{s.artists}</span></th>
                <td className="num">{s.n_sounds}</td>
                <td className="num">{fmtNum(s.uses)}</td>
                <td className="num">{fmtNum(s.views_total)}</td>
                <td className="num">{fmtAvg(s.views_avg)}</td>
                <td className="num" data-testid={`tt-others-uses-${s.song_id}`}>{fmtNum(s.others.uses)}</td>
                <td className="num" data-testid={`tt-others-views-${s.song_id}`}>{fmtNum(s.others.views_total)}</td>
                <td className="num">{fmtNum(s.others.creators)}</td>
                <td className="num">{fmtNum(s.tiktok_uses)}</td>
                <td className="num" data-testid={`tt-coverage-${s.song_id}`}
                  title="Videos we harvested / TikTok's own count of the song's sounds">
                  {s.coverage == null ? "–" : `${Math.round(s.coverage * 100)}%`}</td>
                <td className="num">{fmtNum(s.segment_known)}</td>
                <td>{top ? <a href={top.url} target="_blank" rel="noreferrer">@{top.author ?? "?"} {fmtNum(top.views)}</a> : ""}</td>
                <td>{fmtWhen(s.last_harvest_at)}</td>
                <td>
                  <button type="button" aria-pressed={selected === s.song_id} data-testid={`tt-show-${s.song_id}`}
                    aria-label={`Show segments of ${s.title}`} onClick={() => onPick(s)}>
                    Segments
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableScroll>
  );
}

function SegTable({ rows, label, testId }: { rows: SegRow[]; label: string; testId: string }) {
  return (
    <TableScroll label={`${label} table`}>
      <table className="ws7-table" aria-label={label} data-testid={testId}>
        <thead>
          <tr>
            <th scope="col">Segment</th><th scope="col">Song time</th><th scope="col" className="num">Our videos</th>
            <th scope="col" className="num">Our views</th><th scope="col" className="num">Our avg</th><th scope="col">Our best video</th>
            <th scope="col" className="num">Others' videos</th><th scope="col" className="num">Others' views</th>
            <th scope="col">Others' best video</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} data-testid={`${testId}-${r.key}`}>
              <th scope="row">{r.label}</th>
              <td>{fmtSpan(r.t0_ms, r.t1_ms)}</td>
              <td className="num">{fmtNum(r.uses)}</td>
              <td className="num">{fmtNum(r.views_total)}</td>
              <td className="num">{fmtAvg(r.views_avg)}</td>
              <td>{r.best_item ? <a href={r.best_item.url} target="_blank" rel="noreferrer">@{r.best_item.author ?? "?"} {fmtNum(r.best_item.views)}</a> : ""}</td>
              <td className="num">{fmtNum(r.others_uses)}</td>
              <td className="num">{fmtNum(r.others_views_total)}</td>
              <td>{r.others_best_item ? <a href={r.others_best_item.url} target="_blank" rel="noreferrer">@{r.others_best_item.author ?? "?"} {fmtNum(r.others_best_item.views)}</a> : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

function Segments({ songId }: { songId: number }) {
  const q = useQuery({
    queryKey: qk.list("analytics.tiktok.segments", { songId }),
    queryFn: () => unwrap(api.GET("/api/analytics/tiktok/songs/{song_id}/segments", { params: { path: { song_id: songId } } })),
  });
  const href = `/api/analytics/tiktok/songs/${songId}/segments`;
  return (
    <Section id="tt-segments" title={q.data ? `Segments: ${q.data.title}` : `Segments of song #${songId}`} actions={<Csv href={href} what="segments" />}>
      <QueryView q={q} what="segments">
        {(d) => {
          const views = (d.series ?? []).map((p) => p.views);
          const last = views.at(-1);
          return (
            <>
              <p data-testid="tt-seg-summary">
                Ours: {fmtNum(d.uses)} videos, {fmtNum(d.views_total)} views (avg {fmtAvg(d.views_avg) || "–"}). Other creators on
                our sounds (not in our views): {fmtNum(d.others_uses)} videos, {fmtNum(d.others_views_total)} views.{" "}
                <Sparkline values={views} testId="tt-series"
                  label={views.length ? `Views of ${d.title} over time: ${views.length} samples, latest ${fmtNum(last)}` : `No view history of ${d.title} yet`} />
              </p>
              <h3>By section</h3>
              <SegTable rows={[...(d.sections ?? []), d.unknown]} label={`Use per section of ${d.title}`} testId="tt-sections" />
              <h3>By song window</h3>
              {(d.windows ?? []).length === 0 ? <Empty>No video's song window is known yet (needs located audio).</Empty> : (
                <SegTable rows={d.windows ?? []} label={`Use per song window of ${d.title}`} testId="tt-windows" />
              )}
              {(d.series ?? []).length > 0 && (
                <TableScroll label="Views over time table">
                  <table className="ws7-table" aria-label={`Views of ${d.title} over time`} data-testid="tt-series-table">
                    <thead><tr><th scope="col">When</th><th scope="col" className="num">Views</th></tr></thead>
                    <tbody>{(d.series ?? []).map((p) => <tr key={p.at}><td>{fmtWhen(p.at)}</td><td className="num">{fmtNum(p.views)}</td></tr>)}</tbody>
                  </table>
                </TableScroll>
              )}
            </>
          );
        }}
      </QueryView>
    </Section>
  );
}

function OthersList({ songId }: { songId: number }) {
  const q = useQuery({
    queryKey: qk.list("analytics.tiktok.others", { songId }),
    queryFn: () => unwrap(api.GET("/api/analytics/tiktok/songs/{song_id}/others", { params: { path: { song_id: songId } } })),
  });
  const href = `/api/analytics/tiktok/songs/${songId}/others`;
  return (
    <Section id="tt-others" title="Other creators on our sounds (not our views)" actions={<Csv href={href} what="other creators' videos" />}>
      <QueryView q={q} what="other creators' videos">
        {(d: Others) => ((d.items ?? []).length === 0 ? <Empty>No other creator's video on these sounds yet.</Empty> : (
          <>
            <p data-testid="tt-others-summary">
              {fmtNum(d.uses)} videos by {fmtNum((d.creators ?? []).length)} creators, {fmtNum(d.views_total)} views
              (avg {fmtAvg(d.views_avg) || "–"}). Tracked for awareness; none of this counts as our views.
            </p>
            <h3>Creators</h3>
            <TableScroll label="Other creators table">
              <table className="ws7-table" aria-label={`Other creators using the sounds of ${d.title}`} data-testid="tt-creators-table">
                <thead><tr><th scope="col">Creator</th><th scope="col" className="num">Videos</th><th scope="col" className="num">Views</th></tr></thead>
                <tbody>
                  {(d.creators ?? []).slice(0, 50).map((c) => (
                    <tr key={c.author}><th scope="row"><a href={`https://www.tiktok.com/@${c.author}`} target="_blank" rel="noreferrer">@{c.author}</a></th>
                      <td className="num">{fmtNum(c.videos)}</td><td className="num">{fmtNum(c.views)}</td></tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
            <h3>Videos</h3>
            <TableScroll label="Other creators' videos table">
              <table className="ws7-table" aria-label={`Other creators' videos on the sounds of ${d.title}`} data-testid="tt-others-table">
                <thead><tr><th scope="col">Video</th><th scope="col" className="num">Views</th><th scope="col" className="num">Likes</th>
                  <th scope="col">Song time</th><th scope="col">Posted</th></tr></thead>
                <tbody>
                  {(d.items ?? []).slice(0, 200).map((i) => (
                    <tr key={i.item_id}><th scope="row"><a href={i.url} target="_blank" rel="noreferrer">@{i.author ?? "?"}</a>
                      {i.description ? <span className="ws7-muted"> {i.description.slice(0, 60)}</span> : null}</th>
                      <td className="num">{fmtNum(i.views)}</td><td className="num">{fmtNum(i.likes)}</td>
                      <td>{fmtSpan(i.segment_ms_from, i.segment_ms_to)}</td><td>{fmtWhen(i.created_at)}</td></tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          </>
        ))}
      </QueryView>
    </Section>
  );
}

function Accounts() {
  const q = useQuery({ queryKey: qk.list("analytics.tiktok.accounts"), queryFn: () => unwrap(api.GET("/api/analytics/tiktok/accounts")) });
  return (
    <Section id="tt-accounts" title="Profiles (artists and fan pages)" actions={<Csv href="/api/analytics/tiktok/accounts" what="accounts" />}>
      <QueryView q={q} what="profiles">
        {(rows: Account[]) => (rows.length === 0 ? <Empty>No watched TikTok profile yet.</Empty> : (
          <TableScroll label="TikTok profiles table">
            <table className="ws7-table" aria-label="Watched TikTok profiles" data-testid="tt-accounts-table">
              <thead>
                <tr>
                  <th scope="col">Profile</th><th scope="col">Kind</th><th scope="col" className="num">Followers</th><th scope="col">Trend</th>
                  <th scope="col" className="num">Videos</th><th scope="col" className="num">Posts known</th><th scope="col" className="num">Views</th>
                  <th scope="col" className="num">Avg views</th><th scope="col">Last harvest</th><th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const f = (a.history ?? []).map((p) => p.followers).filter((x): x is number => x != null);
                  return (
                    <tr key={a.handle} data-testid={`tt-account-${a.handle}`}>
                      <th scope="row"><a href={a.url} target="_blank" rel="noreferrer">@{a.handle}</a>{a.enabled ? "" : " (not watched)"}</th>
                      <td>{a.kind === "fanpage" ? "fan page" : `artist${a.artist ? ` (${a.artist})` : ""}`}</td>
                      <td className="num">{fmtNum(a.followers)}</td>
                      <td><Sparkline values={f} label={`Followers of @${a.handle}: ${f.map((x) => fmtNum(x)).join(", ") || "no data"}`} /></td>
                      <td className="num">{fmtNum(a.videos)}</td>
                      <td className="num">{fmtNum(a.posts)}</td>
                      <td className="num">{fmtNum(a.views_total)}</td>
                      <td className="num">{fmtAvg(a.views_avg)}</td>
                      <td>{fmtWhen(a.harvested_at)}</td>
                      <td>{a.status ?? "not harvested yet"}{a.message ? <span className="ws7-muted"> {a.message}</span> : null}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        ))}
      </QueryView>
    </Section>
  );
}

export function TikTokView() {
  const announce = useAnnounce();
  const { sp, set: setUrl } = useUrlState();
  const p = parseTikTokParams(sp);
  const songs = useQuery({ queryKey: qk.list("analytics.tiktok.songs"), queryFn: () => unwrap(api.GET("/api/analytics/tiktok/songs")) });
  return (
    <div className="ws7-page" data-testid="tiktok-analytics-page">
      <PageHeader title="TikTok analytics" md={{ page: "tiktok-analytics", params: { song: p.song } }} />
      <p className="ws7-muted">
        Our videos / views = posts by our handles (the artists' TikTok profiles and our posting accounts) on the song's sounds.
        Others = videos other creators made with our sounds: tracked separately, never counted in our views. TikTok uses = TikTok's
        own video count of those sounds. Harvested twice a day.
      </p>
      <Section id="tt-songs" title="Songs" actions={<Csv href="/api/analytics/tiktok/songs" what="songs" />}>
        <QueryView q={songs} what="songs">
          {(rows) => (rows.length === 0 ? <Empty>No song has a TikTok sound yet.</Empty> : (
            <SongsTable rows={sortSongs(rows, p.sort)} sort={p.sort} selected={p.song}
              onSort={(s) => {
                setUrl(tiktokParamsToPatch({ ...p, sort: s }));
                announce(`Songs sorted by ${s === "avg" ? "our average views" : s === "others" ? "other creators' views" : `our ${s}`}`);
              }}
              onPick={(s) => {
                setUrl({ song: s.song_id });
                announce(`Showing segments of ${s.title}`);
              }} />
          ))}
        </QueryView>
      </Section>
      {p.song != null && <Segments key={p.song} songId={p.song} />}
      {p.song != null && <OthersList key={`o${p.song}`} songId={p.song} />}
      <Accounts />
    </div>
  );
}
